import type { ReactNode } from 'react'

/**
 * Der Rahmen einer Buchseite: A5 hoch, mit Fuss und Seitenzahl.
 *
 * Jede gedruckte Seite ist genau ein solches Element. Das ist die Entscheidung,
 * an der das ganze Druckbild hängt: die App paginiert selbst, statt einen
 * langen Textfluss vom Browser umbrechen zu lassen. Der Preis ist, dass die
 * Textmengen vorher abgeschätzt werden müssen (siehe stagePlan.ts). Der Gewinn
 * ist, dass jede Seite verlässlich aussieht, wie sie aussehen soll - und dass
 * die Seitenzahl einfach als Text dasteht.
 *
 * Warum nicht die Margin-Boxen von @page: counter(page) darin funktioniert
 * zwar, aber counter(pages) für das "von N" ist historisch unzuverlässig,
 * Seitenzahlen in position:fixed zeigen in Chromium konstant 1, und laufende
 * Kopfzeilen über Margin-Boxen gibt es erst ab Chromium 131. Wer ohnehin je
 * Seite ein Element hat, braucht davon nichts.
 */
export interface PageFrameProps {
  /** Art der Seite. Wird zur Klasse und steuert das Innenleben in print.css. */
  kind: string
  /** Seitenzahl, wie sie im Fuss steht. Ohne Angabe bleibt der Fuss leer. */
  page?: number
  /** Text links im Fuss, meist der Reisetitel. */
  fuss?: string
  /**
   * Laufende Kopfzeile.
   *
   * Der Fortsetzungsmarker: eine Etappe, die auf einer zweiten Seite
   * weitergeht, sagt dort oben, zu welchem Tag sie gehört. Ohne ihn blättert
   * man in ein Textstück ohne Anfang.
   */
  laufend?: string
  /**
   * Was rechts in der laufenden Kopfzeile steht.
   *
   * Voreingestellt "Fortsetzung", denn das ist der Regelfall. Eine
   * Roadbookseite ist aber keine Fortsetzung des Tagebuchs, sondern etwas
   * anderes - und wer blättert, soll das oben rechts lesen und nicht aus dem
   * Inhalt schliessen müssen.
   */
  marke?: string
  children: ReactNode
}

export function PageFrame({
  kind,
  page,
  fuss,
  laufend,
  marke = 'Fortsetzung',
  children,
}: PageFrameProps) {
  return (
    <article className={`dr-seite dr-seite--${kind}`}>
      {laufend && (
        <div className="dr-lauf">
          <span>{laufend}</span>
          <span className="dr-lauf-marke">{marke}</span>
        </div>
      )}

      <div className="dr-koerper">{children}</div>

      {page !== undefined && (
        <footer className="dr-fuss">
          <span className="dr-fuss-text">{fuss}</span>
          <span className="dr-nummer">{page}</span>
        </footer>
      )}
    </article>
  )
}

/**
 * Linien zum Vollschreiben.
 *
 * Als Elemente mit Unterkante und nicht als gestreifter Hintergrund: ein
 * repeating-linear-gradient wäre weniger Markup, würde aber nur gedruckt,
 * wenn im Druckdialog "Hintergrundgrafiken" angehakt ist. Eine Notizseite ohne
 * Linien ist ein leeres Blatt.
 *
 * Die Zahl ist eine Obergrenze, kein Versprechen: der Kasten nimmt sich den
 * Platz, der übrig ist, und schneidet ab, was darüber hinausgeht.
 */
export function NotizLinien({ anzahl }: { anzahl: number }) {
  return (
    <div className="dr-linien" aria-hidden="true">
      {Array.from({ length: anzahl }, (_, i) => (
        <span key={i} />
      ))}
    </div>
  )
}

/**
 * Der übrige Platz einer Seite, liniert.
 *
 * Die eigentliche Antwort auf ungleiche Inhaltsmengen: was nicht gefüllt ist,
 * bleibt nicht leer, sondern wird zum Platz zum Schreiben. Eine Etappe ohne
 * Eintrag ergibt so eine Seite zum Vollschreiben statt einer Lücke, und die
 * Zahlenseite bekommt Raum für das, was unterwegs dazukommt.
 */
export function NotizFeld({ titel, anzahl = 20 }: { titel: string; anzahl?: number }) {
  return (
    <section className="dr-fuellung">
      <h3>{titel}</h3>
      <NotizLinien anzahl={anzahl} />
    </section>
  )
}
